import React from 'react';
import { useTranslation } from "react-i18next";
import { CopyToClipboard } from "react-copy-to-clipboard";
import ShareComponent from './shareComponent';
import Icon from '../../../jo-components/ui/icon/icon';
import Button from '../../../jo-components/ui/button/button';
import './style/urlContainer.css';
import Message from '../../../jo-components/misc/message/message';
import Modal from '../../../jo-components/ui/modal/modal';

function RotateLinkModal({ data, showModal, setShowModal, onSubmit }) {
    const { t } = useTranslation();

    const rotateLinkAction = data?.resale_option?.rotate_link_action;

    const rotateResaleLink = React.useCallback(() => {
        onSubmit({
            data: {
                action: 'RotateResaleLinkStep'
            }
        });
        setShowModal(false);
    }, [onSubmit]);

    if (!showModal || !rotateLinkAction) {
        return null;
    }

    return (
        <Modal 
            title={rotateLinkAction.modal_title} 
            open={showModal} 
            onClose={() => setShowModal(false)}
            cancelButton={
                <Button 
                    type='inverted-secondary' 
                    label={t('widget.input.crop-modal.cta-cancel')} 
                    onClick={() => setShowModal(false)}
                />
            }
            submitButton={
                <Button 
                    label={t('widget.resale.breakdown.cta-confirm')} 
                    onClick={rotateResaleLink}
                />
            }
        >
            <Message 
                type="warning" 
                title={rotateLinkAction.modal_title} 
                description={rotateLinkAction.modal_body} 
            />
        </Modal>
    );
}

export default function UrlContainer({ data, onSubmit = () => {} }) {
    const { t } = useTranslation();
    const timeoutRef = React.useRef(null);
    const [showShareModal, setShowShareModal] = React.useState(false);
    const [showRotateModal, setShowRotateModal] = React.useState(false);
    const [copied, setCopied] = React.useState(false);

    const resaleOption = data?.resale_option;
    const hasRotateAction = !!resaleOption?.rotate_link_action;
    const hasShareAction = !!resaleOption?.share_link_action;

    const handleCopy = React.useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setCopied(true);
        timeoutRef.current = setTimeout(() => setCopied(false), 2000);
    }, [timeoutRef]);

    return (
        <>
            <div className='wz-url-container'>
                <div className='wz-breakdown-left-content'>
                    <div className='wz-breakdown-icon'>
                        <Icon name='lock' size='huge'/>
                    </div>
                    <div>
                        <div className='wz-detail-label'>{resaleOption.title}</div>
                        {resaleOption.description && (
                            <div className='wz-detail-description'>{resaleOption.description}</div>
                        )}
                    </div>
                </div>
                <div className='wz-resale_link'>
                    <input 
                        disabled
                        className='wz-copy-to-clipboard-input' 
                        value={resaleOption.url} 
                        readOnly
                    />
                    <CopyToClipboard 
                        text={resaleOption.url}
                        onCopy={handleCopy}
                    >
                        <Button 
                            className='wz-action-button'
                            label={
                                <div>
                                    <Icon name={copied ? 'checkSimple' : 'copy'} />
                                    <span className='wz-action-item-label'>
                                        {t('widget.resale.url_container.copy')}
                                    </span>
                                </div>
                            }
                        />
                    </CopyToClipboard>
                    {hasRotateAction && (
                        <Button
                            className='wz-action-button'
                            label={
                                <div>
                                    <Icon name='refresh' />
                                    <span className='wz-action-item-label'>
                                        {t('widget.resale.url_container.refresh')}
                                    </span>
                                </div>
                            }
                            onClick={() => setShowRotateModal(true)}
                        />
                    )}
                    {hasShareAction && (
                        <Button
                            className='wz-action-button'
                            label={
                                <div>
                                    <Icon name='sendMail' />
                                    <span className='wz-action-item-label'>
                                        {t('widget.resale.url_container.send')}
                                    </span>
                                </div>
                            }
                            onClick={() => setShowShareModal(true)}
                        />
                    )}
                </div>
            </div>
            {hasShareAction && (
                <ShareComponent 
                    data={data} 
                    onSubmit={onSubmit} 
                    showModal={showShareModal} 
                    setShowModal={setShowShareModal}
                />
            )}
            {hasRotateAction && (
                <RotateLinkModal
                    data={data}
                    onSubmit={onSubmit}
                    showModal={showRotateModal} 
                    setShowModal={setShowRotateModal} 
                />
            )}
        </>
    );
}